import { useReducer } from 'react';

import { IAction } from 'src/utils/ts-utilities';
import { LegalContextEnum, AgreementConditionEnum, JurisdictionEnum, LiabilityEnum } from 'src/graphql/schema';

enum ActionEnum {
  SET_INITIAL_DATA = 'set_initial_data',
  UPDATE_CONTEXT = 'update_context',
  UPDATE_OTHER_CONTEXT = 'update_other_context',
  UPDATE_CONDITIONS = 'update_conditions',
  UPDATE_OTHER_CONDITION = 'update_other_condition',
  UPDATE_JURISDICTION = 'update_jurisdiction',
  UPDATE_LIABILITY = 'update_liability',
  RESET_STORE = 'reset_store',
}

type IActionReducer =
  | IAction<ActionEnum.SET_INITIAL_DATA, IDataState>
  | IAction<ActionEnum.UPDATE_CONTEXT, { values: LegalContextEnum[]; hasChanges: boolean }>
  | IAction<ActionEnum.UPDATE_OTHER_CONTEXT, { value: string; hasChanges: boolean }>
  | IAction<ActionEnum.UPDATE_CONDITIONS, { values: AgreementConditionEnum[]; hasChanges: boolean }>
  | IAction<ActionEnum.UPDATE_OTHER_CONDITION, { value: string; hasChanges: boolean }>
  | IAction<ActionEnum.UPDATE_JURISDICTION, { value: JurisdictionEnum; hasChanges: boolean }>
  | IAction<ActionEnum.UPDATE_LIABILITY, { value: LiabilityEnum; hasChanges: boolean }>
  | IAction<ActionEnum.RESET_STORE>;
interface IDataState {
  context: LegalContextEnum[];
  otherContext: string;
  jurisdiction?: JurisdictionEnum;
  liability?: LiabilityEnum;
  conditions: AgreementConditionEnum[];
  conditionsOther: string;
}
interface IState {
  data: IDataState;
  validation: Record<
    string,
    Partial<{
      isRequired?: boolean;
      hasChanges: boolean;
    }>
  >;
}

const initialState: IState = {
  data: {
    context: [],
    otherContext: '',
    jurisdiction: undefined,
    liability: undefined,
    conditions: [],
    conditionsOther: '',
  },
  validation: {
    context: {
      isRequired: false,
      hasChanges: false,
    },
    otherContext: {
      isRequired: false,
      hasChanges: false,
    },
    deposit: {
      isRequired: false,
      hasChanges: false,
    },
    otherDeposit: {
      isRequired: false,
      hasChanges: false,
    },
    conditions: {
      isRequired: false,
      hasChanges: false,
    },
    conditionsOther: {
      isRequired: false,
      hasChanges: false,
    },
  },
};

const reducer = (state: IState, action: IActionReducer): IState => {
  switch (action.type) {
    case ActionEnum.SET_INITIAL_DATA:
      return {
        ...state,
        data: {
          ...state.data,
          ...action.payload,
        },
      };
    case ActionEnum.UPDATE_CONTEXT: {
      const filteredContext = action.payload.values.filter((v) => v !== LegalContextEnum.Other);
      const isOtherContextIncluded = action.payload.values.includes(LegalContextEnum.Other);

      return {
        ...state,
        data: {
          ...state.data,
          context: action.payload.values,
        },
        validation: {
          ...state.validation,
          context: {
            isRequired: !action.payload.values.length,
            hasChanges: action.payload.hasChanges && Boolean(filteredContext.length),
          },
          otherContext: {
            isRequired: isOtherContextIncluded && !state.data.otherContext,
            hasChanges: isOtherContextIncluded && !!state.data.otherContext,
          },
        },
      };
    }
    case ActionEnum.UPDATE_OTHER_CONTEXT:
      return {
        ...state,
        data: {
          ...state.data,
          otherContext: action.payload.value.trim(),
        },
        validation: {
          ...state.validation,
          otherContext: {
            isRequired: state.data.context.includes(LegalContextEnum.Other) && !action.payload.value.trim(),
            hasChanges: action.payload.hasChanges,
          },
        },
      };
    case ActionEnum.UPDATE_CONDITIONS: {
      const filteredConditions = action.payload.values.filter((v) => v !== AgreementConditionEnum.Other);
      const isOtherConditionIncluded = action.payload.values.includes(AgreementConditionEnum.Other);

      return {
        ...state,
        data: {
          ...state.data,
          conditions: action.payload.values,
        },
        validation: {
          ...state.validation,
          conditions: {
            isRequired: !action.payload.values.length,
            hasChanges: action.payload.hasChanges && Boolean(filteredConditions.length),
          },
          conditionsOther: {
            ...state.validation.conditionsOther,
            isRequired: isOtherConditionIncluded && !state.data.conditionsOther,
            hasChanges: isOtherConditionIncluded && !!state.data.conditionsOther,
          },
        },
      };
    }
    case ActionEnum.UPDATE_OTHER_CONDITION:
      return {
        ...state,
        data: {
          ...state.data,
          conditionsOther: action.payload.value.trim(),
        },
        validation: {
          ...state.validation,
          conditionsOther: {
            isRequired: state.data.conditions.includes(AgreementConditionEnum.Other) && !action.payload.value.trim(),
            hasChanges: action.payload.hasChanges,
          },
        },
      };
    case ActionEnum.UPDATE_JURISDICTION:
      return {
        ...state,
        data: {
          ...state.data,
          jurisdiction: action.payload.value,
        },
        validation: {
          ...state.validation,
          jurisdiction: {
            isRequired: false,
            hasChanges: action.payload.hasChanges,
          },
        },
      };
    case ActionEnum.UPDATE_LIABILITY:
      return {
        ...state,
        data: {
          ...state.data,
          liability: action.payload.value,
        },
        validation: {
          ...state.validation,
          liability: {
            isRequired: false,
            hasChanges: action.payload.hasChanges,
          },
        },
      };
    case ActionEnum.RESET_STORE:
      return initialState;
    default:
      return state;
  }
};

export const useAgreementSuggestChangesSlice = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setInitialData = (payload: IDataState) => dispatch({ type: ActionEnum.SET_INITIAL_DATA, payload });

  const updateContext = (payload: { values: LegalContextEnum[]; hasChanges: boolean }) =>
    dispatch({ type: ActionEnum.UPDATE_CONTEXT, payload });

  const updateOtherContext = (payload: { value: string; hasChanges: boolean }) =>
    dispatch({
      type: ActionEnum.UPDATE_OTHER_CONTEXT,
      payload,
    });

  const updateConditions = (payload: { values: AgreementConditionEnum[]; hasChanges: boolean }) =>
    dispatch({ type: ActionEnum.UPDATE_CONDITIONS, payload });

  const updateOtherCondition = (payload: { value: string; hasChanges: boolean }) =>
    dispatch({ type: ActionEnum.UPDATE_OTHER_CONDITION, payload });

  const updateJurisdiction = (payload: { value: JurisdictionEnum; hasChanges: boolean }) =>
    dispatch({ type: ActionEnum.UPDATE_JURISDICTION, payload });

  const updateLiability = (payload: { value: LiabilityEnum; hasChanges: boolean }) =>
    dispatch({ type: ActionEnum.UPDATE_LIABILITY, payload });

  return {
    state,
    setInitialData,
    updateContext,
    updateOtherContext,
    updateConditions,
    updateOtherCondition,
    updateJurisdiction,
    updateLiability,
  };
};
